import type { TFunction } from 'i18next';
import type { PublicProduct } from '../../api/bookingApi.js';
import { LanguageSwitcher } from '../../../app/components/LanguageSwitcher.js';
import { AppIcon } from '../../ui/Icon';
import { formatPrice, type PriceLocale } from '../../formatting.js';
import { ALL_CATEGORY_KEY } from '../bookingConstants.js';

type BookingProductsPageProps = {
  t: TFunction;
  priceLocale: PriceLocale;
  searchQuery: string;
  selectedCategory: string;
  categories: string[];
  filteredProducts: PublicProduct[];
  selectedProductIds: string[];
  onBack: () => void;
  onSearchChange: (value: string) => void;
  onCategoryChange: (category: string) => void;
  onToggleProduct: (productId: string) => void;
};

export function BookingProductsPage({
  t,
  priceLocale,
  searchQuery,
  selectedCategory,
  categories,
  filteredProducts,
  selectedProductIds,
  onBack,
  onSearchChange,
  onCategoryChange,
  onToggleProduct,
}: BookingProductsPageProps) {
  return (
    <div className="booking-landing-card booking-services-card has-summary">
      <div
        className="booking-p-md-lg booking-gap-md booking-border-b"
        style={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}
      >
        <button
          type="button"
          onClick={onBack}
          className="booking-icon-ghost"
          style={{ padding: 'var(--spacing-sm)', color: 'var(--color-text-primary)' }}
        >
          <AppIcon name="chevronLeft" size={24} />
        </button>
        <h1 className="booking-heading" style={{ margin: 0, flex: 1, textAlign: 'left' }}>
          {t('booking.selectProducts')}
        </h1>
        <LanguageSwitcher />
      </div>

      <div className="booking-p-md-lg booking-border-b" style={{ flexShrink: 0 }}>
        <div className="booking-search-inner">
          <AppIcon name="search" size={20} tone="muted" />
          <input
            type="search"
            inputMode="search"
            enterKeyHint="search"
            autoComplete="off"
            placeholder={t('booking.search')}
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className="booking-search-input"
          />
        </div>
      </div>

      <div
        className="booking-categories-scroll booking-p-md-lg booking-gap-sm booking-border-b"
        style={{ flexShrink: 0 }}
      >
        <button
          type="button"
          onClick={() => onCategoryChange(ALL_CATEGORY_KEY)}
          className={`booking-pill ${selectedCategory === ALL_CATEGORY_KEY ? 'booking-pill-active' : ''}`}
        >
          {t('booking.all')}
        </button>
        {categories.map((cat) => (
          <button
            key={cat}
            type="button"
            onClick={() => onCategoryChange(cat)}
            className={`booking-pill ${selectedCategory === cat ? 'booking-pill-active' : ''}`}
          >
            {cat}
          </button>
        ))}
      </div>

      <div className="booking-services-list" style={{ flex: 1, overflowY: 'auto' }}>
        {filteredProducts.length === 0 && (
          <div className="booking-empty-state">
            {searchQuery ? t('booking.error.nothingFound') : t('booking.error.noProducts')}
          </div>
        )}
        {filteredProducts.map((product) => {
          const isSelected = selectedProductIds.includes(product.id);
          return (
            <div
              key={product.id}
              onClick={() => onToggleProduct(product.id)}
              className="booking-service-item"
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onToggleProduct(product.id);
                }
              }}
            >
              <div className="booking-service-item__content">
                <div className="booking-service-item__title">{product.name}</div>
              </div>
              <div className="booking-service-item__actions">
                <div className="booking-service-item__price">
                  {formatPrice(Number(product.price), priceLocale)} {t('booking.currency')}
                </div>
                <input
                  type="checkbox"
                  checked={isSelected}
                  readOnly
                  aria-label={product.name}
                  className="booking-service-item__checkbox"
                  tabIndex={-1}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
